import React from "react";
import removedKaspa from '../../assets/removedKaspa.json';

const FinanceTable = ({
    outstandingPowerpoolBalance,
    workerFinance,
    miningTotal,
    kasPrice,
    usdToGbpRate,
    financeViewType,
    setFinanceViewType,
    financePriceType,
    setFinancePriceType,
    styles,
    getAllWorkers,
    formatNumber,
    usdToGbp
}) => {
    // Define totalHashRate before any function that uses it
    const totalHashRate = workerFinance ? workerFinance.reduce((sum, wf) => sum + (typeof wf.Hashrate === 'number' ? wf.Hashrate : 0), 0) : 0;
    // Calculate totalKaspaRemovedFromMining from removedKaspa array, only if DateRemoved >= 2026-01-03
    const totalKaspaRemovedFromMining = Array.isArray(removedKaspa)
        ? removedKaspa.filter(entry => {
            const date = new Date(entry.DateRemoved);
            return date >= new Date('2026-01-03');
        }).reduce((sum, entry) => sum + (typeof entry.Quantity === 'number' ? entry.Quantity : 0), 0)
        : 0;
    // Move calculateFinanceFields above all uses
    function calculateFinanceFields(fin, miningTotal) {
        if (!fin) return {};
        const today = new Date();
        const started = parseDateUK(fin.DateStarted);
        let ended = null;
        let daysMining;
        if (fin.DateEnded) {
            ended = parseDateUK(fin.DateEnded);
            daysMining = Math.max(0, Math.floor((ended - started) / (1000 * 60 * 60 * 24)) - (fin.DaysOffline || 0));
        } else {
            daysMining = Math.max(0, Math.floor((today - started) / (1000 * 60 * 60 * 24)) - (fin.DaysOffline || 0));
        }
        let minedTotalRecent = null, minedTotal = null, minedPerDayRecent = null, minedPerDay = null, hashRatePercent = null, earnedToDate = null, stillToEarn = null, earnPerDay = null, profitPerDay = null, daysToROI = null, roiDate = null;
        if (typeof fin.Hashrate === 'number' && totalHashRate > 0) {
            hashRatePercent = fin.Hashrate / totalHashRate;
        }
        // Use priceToUseGBP from closure scope (set in the finance tab block). For ks0s, estimated 10,000 each earned before KS5Ls started (1490 since then) in order to exclude from ROI dates when price is less than about £0.07 (reality is sold at much higher price so already have ROIed)
        if (typeof miningTotal === 'number' && hashRatePercent !== null) {
            // Add totalKaspaRemovedFromMining to miningTotal for minedTotalRecent
            minedTotalRecent = (miningTotal + totalKaspaRemovedFromMining + outstandingPowerpoolBalance) * hashRatePercent;
            //console.log('miningTotal', miningTotal, 'totalKaspaRemovedFromMining', totalKaspaRemovedFromMining, 'outstandingPowerpoolBalance', outstandingPowerpoolBalance, 'hashRatePercent', hashRatePercent, 'minedTotalRecent', minedTotalRecent);
            minedTotal = (fin.KasMinedAlready || 0) + minedTotalRecent;
            // Calculate days since 3 Jan 2026
            const dateOfRecentMiner = new Date(2026, 0, 3); // Month is 0-indexed
            const daysSincedateOfRecentMiner = Math.max(1, Math.floor((today - dateOfRecentMiner) / (1000 * 60 * 60 * 24)));
            if (!fin.DateEnded) {
              minedPerDayRecent = minedTotalRecent !== null && daysSincedateOfRecentMiner > 0 ? minedTotalRecent / daysSincedateOfRecentMiner : null;
            } else {
              minedPerDayRecent = 0;
            }
            minedPerDay = (daysMining + (fin.DaysOffline || 0)) > 0 ? minedTotal / (daysMining + (fin.DaysOffline || 0)) : null;
            // EarnedToDate in GBP using priceToUseGBP
            if (typeof priceToUseGBP === 'number') {
                earnedToDate = minedTotal * priceToUseGBP;
            }
            // StillToEarn = PurchaseCost - EarnedToDate, but not less than 0
            if (typeof fin.PurchaseCost === 'number' && typeof earnedToDate === 'number') {
                stillToEarn = fin.PurchaseCost - earnedToDate;
                if (stillToEarn < 0) stillToEarn = 0;
            }
            earnPerDay = minedPerDayRecent*priceToUseGBP//(earnedToDate / daysMining );
            profitPerDay = earnPerDay - (fin.ElectricCostPerDayGBP || 0);
            // daysToROI and roiDate
            if (stillToEarn === 0) {
                daysToROI = 0;
            } else if (typeof stillToEarn === 'number' && typeof profitPerDay === 'number' && profitPerDay > 0) {
                daysToROI = stillToEarn / profitPerDay;
            }
            if (typeof daysToROI === 'number' && daysToROI > 0) {
                const roi = new Date(today.getTime() + daysToROI * 24 * 60 * 60 * 1000);
                roiDate = roi.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
            } else if (daysToROI === 0) {
                roiDate = '✅';
            }
        }
        return {
            DaysMining: daysMining,
            HashRatePercent: hashRatePercent,
            MinedTotalRecent: minedTotalRecent,
            MinedTotal: minedTotal,
            MinedPerDayRecent: minedPerDayRecent,
            MinedPerDay: minedPerDay,
            EarnedToDate: earnedToDate,
            StillToEarn: stillToEarn,
            EarnPerDay: earnPerDay,
            ProfitPerDay: profitPerDay,
            DaysToROI: daysToROI,
            ROIDate: roiDate
        };
    }
    // Always define kheavyhashWorkers first
    // Show all kheavyhash workers in finance tab (including DateEnded)
    const kheavyhashWorkers = getAllWorkers().filter(w => w.algorithm === 'kheavyhash');
    // Price options and card must be defined here for scope
    const currentGBP = (typeof kasPrice === 'number' && typeof usdToGbpRate === 'number')
        ? (kasPrice * usdToGbpRate).toFixed(4)
        : '-';
    const doubleCurrentGBP = (typeof kasPrice === 'number' && typeof usdToGbpRate === 'number')
        ? (kasPrice * usdToGbpRate * 2).toFixed(4)
        : '-';
    const priceOptions = [
        { label: `Current (£${currentGBP})`, value: 'Current' },
        { label: `Double Current (£${doubleCurrentGBP})`, value: 'Double' },
        { label: '£0.05', value: '0.05' },
        { label: '£0.10', value: '0.10' },
        { label: '£0.20', value: '0.20' },
        { label: '£0.25', value: '0.25' },
        { label: '£0.50', value: '0.50' },
        { label: '£1.00', value: '1.00' }
    ];
    let priceToUseGBP = (typeof kasPrice === 'number' && typeof usdToGbpRate === 'number') ? kasPrice * usdToGbpRate : 0.25;
    if (financePriceType === 'Double') priceToUseGBP *= 2;
    else if (!isNaN(Number(financePriceType))) priceToUseGBP = Number(financePriceType);
    const financeOptionsCard = (
        <div className="box" style={{ marginBottom: '1.5rem', background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '8px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'center' }}>
                <div>
                    <label style={{ fontWeight: 500, marginRight: 8 }}>View Type:</label>
                    <select value={financeViewType} onChange={e => setFinanceViewType(e.target.value)} style={{ background: '#334155', color: '#e2e8f0', borderRadius: 4, padding: '0.5rem' }}>
                        <option value="Full">Full</option>
                        <option value="Detailed">Detailed</option>
                        <option value="Standard">Standard</option>
                    </select>
                </div>
                <div>
                    <label style={{ fontWeight: 500, marginRight: 8 }}>Price to Use:</label>
                    <select value={financePriceType} onChange={e => setFinancePriceType(e.target.value)} style={{ background: '#334155', color: '#e2e8f0', borderRadius: 4, padding: '0.5rem' }}>
                        {priceOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
    // Define columns for each view type
    const columns = {
        Full: [
            { key: 'Name', label: 'Name' },
            { key: 'Host', label: 'Host' },
            { key: 'ElectricCostPerDay', label: 'Electric $/Day' },
            { key: 'ElectricCostPerDayGBP', label: 'Electric £/Day' },
            { key: 'Hashrate', label: 'Hashrate' },
            { key: 'HashRatePercent', label: 'Hashrate %' },
            { key: 'DateStarted', label: 'Date Started' },
            { key: 'DaysMining', label: 'Days Mining' },
            { key: 'DaysOffline', label: 'Offline Days' },
            { key: 'KasMinedAlready', label: 'KAS Mined (Old)' },
            { key: 'MinedTotalRecent', label: 'Mined (Recent)' },
            { key: 'MinedTotal', label: 'Mined (Total)' },
            { key: 'MinedPerDayRecent', label: 'Mined/Day (Recent)' },
            { key: 'BreakEvenKaspa', label: 'Break-even KAS (£)', style: { minWidth: 90 } },
            { key: 'MinedPerDay', label: 'Mined/Day (Total)' },
            { key: 'PurchaseCost', label: 'Purchase £' },
            { key: 'EarnedToDate', label: 'Earned To Date (£)' },
            { key: 'StillToEarn', label: 'Still To Earn (£)' },
            { key: 'EarnPerDay', label: 'Earn/Day (£)' },
            { key: 'ProfitPerDay', label: 'Profit/Day (£)' },
            { key: 'DaysToROI', label: 'Days to ROI' },
            { key: 'ROIDate', label: 'ROI Date', style: { minWidth: 110 } }
        ],
        Detailed: [
            { key: 'Name', label: 'Name' },
            { key: 'Host', label: 'Host' },
            { key: 'ElectricCostPerDay', label: 'Electric $/Day' },
            { key: 'Hashrate', label: 'Hashrate' },
            { key: 'DateStarted', label: 'Date Started' },
            { key: 'MinedPerDayRecent', label: 'Mined/Day (Recent)' },
            { key: 'BreakEvenKaspa', label: 'Break-even KAS (£)', style: { minWidth: 90 } },
            { key: 'ProfitPerDay', label: 'Profit/Day (£)' },
            { key: 'ROIDate', label: 'ROI Date', style: { minWidth: 110 } }
        ],
        Standard: [
            { key: 'Name', label: 'Name' },
            { key: 'Host', label: 'Host' },
            { key: 'Hashrate', label: 'Hashrate' },
            { key: 'DateStarted', label: 'Date Started' },
            { key: 'MinedPerDayRecent', label: 'Mined/Day (Recent)' },
            { key: 'ProfitPerDay', label: 'Profit/Day (£)' },
            { key: 'ROIDate', label: 'ROI Date', style: { minWidth: 110 } }
        ]
    };
        // Helper to calculate break-even KASPA price (GBP) using MinedPerDayRecent
        function getBreakEvenKaspaPrice(fin, minedPerDayRecent) {
            if (!fin || typeof fin.ElectricCostPerDayGBP !== 'number' || !minedPerDayRecent || minedPerDayRecent === 0) {
                return null;
            }
            return fin.ElectricCostPerDayGBP / minedPerDayRecent;
        }
    const selectedColumns = columns[financeViewType] || columns.Full;
    return (
        <>
            {financeOptionsCard}
            <div className="box mt-5" style={styles.tableBox}>
                <h2 className="title is-5 mb-4" style={styles.tableTitle}>Kheavyhash Workers</h2>
                <div className="table-container">
                    <table className="table is-fullwidth is-hoverable" style={styles.table}>
                        <thead>
                            <tr>
                                {selectedColumns.map((col, colIdx) => {
                                    let style = { ...styles.tableHeader };
                                    if (colIdx > selectedColumns.findIndex(c => c.key === 'Host')) {
                                        style = { ...styles.tableHeader, textAlign: 'right' };
                                    }
                                    if (col.style) {
                                        style = { ...style, ...col.style };
                                    }
                                    return (
                                        <th key={col.key} style={style}>{col.label}</th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                // Totals logic
                                let totalHashrate = 0, totalElectric = 0, totalKasMinedOld = 0, totalMinedRecent = 0, totalMinedTotal = 0, totalMinedPerDayRecent = 0, totalMinedPerDay = 0, totalPurchase = 0, totalEarnedToDate = 0, totalStillToEarn = 0, totalEarnPerDay = 0, totalProfitPerDay = 0;
                                let countMinedPerDayRecent = 0, countMinedPerDay = 0;
                                let totalBreakEvenNumerator = 0, totalBreakEvenDenominator = 0;
                                const rows = kheavyhashWorkers.map((worker, index) => {
                                    const fin = getFinanceForWorker(worker);
                                    const calc = calculateFinanceFields(fin, miningTotal);
                                    // For overall break-even: sum numerator and denominator
                                    if (fin && typeof fin.ElectricCostPerDayGBP === 'number' && calc.MinedPerDayRecent && calc.MinedPerDayRecent > 0) {
                                        totalBreakEvenNumerator += fin.ElectricCostPerDayGBP;
                                        totalBreakEvenDenominator += calc.MinedPerDayRecent;
                                    }
                                    if (fin) {
                                        totalHashrate += typeof fin.Hashrate === 'number' ? fin.Hashrate : 0;
                                        totalElectric += typeof fin.ElectricCostPerDay === 'number' ? fin.ElectricCostPerDay : 0;
                                        totalKasMinedOld += typeof fin.KasMinedAlready === 'number' ? fin.KasMinedAlready : 0;
                                    }
                                    if (calc.MinedTotalRecent !== null && calc.MinedTotalRecent !== undefined) totalMinedRecent += calc.MinedTotalRecent;
                                    if (calc.MinedTotal !== null && calc.MinedTotal !== undefined) totalMinedTotal += calc.MinedTotal;
                                    if (calc.MinedPerDayRecent !== null && calc.MinedPerDayRecent !== undefined) { totalMinedPerDayRecent += calc.MinedPerDayRecent; countMinedPerDayRecent++; }
                                    if (calc.MinedPerDay !== null && calc.MinedPerDay !== undefined) { totalMinedPerDay += calc.MinedPerDay; countMinedPerDay++; }
                                    if (calc.EarnedToDate !== null && calc.EarnedToDate !== undefined) totalEarnedToDate += calc.EarnedToDate;
                                    if (calc.StillToEarn !== null && calc.StillToEarn !== undefined) totalStillToEarn += calc.StillToEarn;
                                    if (fin?.PurchaseCost !== null && fin?.PurchaseCost !== undefined) totalPurchase += fin.PurchaseCost;
                                    if (calc.EarnPerDay !== null && calc.EarnPerDay !== undefined) totalEarnPerDay += calc.EarnPerDay;
                                    if (calc.ProfitPerDay !== null && calc.ProfitPerDay !== undefined) totalProfitPerDay += calc.ProfitPerDay;
                                    return (
                                        <tr key={index}>
                                            {selectedColumns.map((col, colIdx) => {
                                                // Right justify all columns after 'Host'
                                                let style = { ...styles.tableCell };
                                                if (colIdx > selectedColumns.findIndex(c => c.key === 'Host')) {
                                                    style = { ...styles.tableCell, textAlign: 'right' };
                                                }
                                                if (col.style) {
                                                    style = { ...style, ...col.style };
                                                }
                                                switch (col.key) {
                                                                                                        case 'BreakEvenKaspa': {
                                                                                                            const breakEven = getBreakEvenKaspaPrice(fin, calc.MinedPerDayRecent);
                                                                                                            return <td key={col.key} style={style}>{breakEven ? `£${breakEven.toFixed(2)}` : ''}</td>;
                                                                                                        }
                                                    case 'Name':
                                                        // Use same display logic as WorkersTable.js
                                                        return (
                                                            <td key={col.key} style={style}>
                                                                {(() => {
                                                                    const rawName = worker.name.replace('jjphotos.', '');
                                                                    if (rawName.startsWith('ks0')) {
                                                                        const suffix = rawName.slice(3);
                                                                        let display = 'ks0';
                                                                        if (suffix === 'Neville') display = 'ks02';
                                                                        else if (suffix === 'Irwin') display = 'ks03';
                                                                        else if (suffix === 'Ronaldo') display = 'ks07';
                                                                        return <span title={suffix}>{display}</span>;
                                                                    }
                                                                    return rawName;
                                                                })()}
                                                            </td>
                                                        );
                                                    case 'Host':
                                                        return <td key={col.key} style={style}>{fin?.Host || ''}</td>;
                                                    case 'ElectricCostPerDay':
                                                        return <td key={col.key} style={style}>{fin?.ElectricCostPerDay ? `$${fin.ElectricCostPerDay.toFixed(2)}` : ''}</td>;
                                                    case 'ElectricCostPerDayGBP':
                                                        return <td key={col.key} style={style}>{fin?.ElectricCostPerDayGBP ? `£${fin.ElectricCostPerDayGBP.toFixed(2)}` : ''}</td>;
                                                    case 'Hashrate':
                                                        return <td key={col.key} style={style}>{fin?.Hashrate ? `${fin.Hashrate.toFixed(1)} TH/s` : ''}</td>;
                                                    case 'HashRatePercent':
                                                        return <td key={col.key} style={style}>{calc?.HashRatePercent !== null && calc?.HashRatePercent !== undefined ? `${(calc.HashRatePercent * 100).toFixed(1)}%` : ''}</td>;
                                                    case 'DateStarted':
                                                        return <td key={col.key} style={style}>{fin?.DateStarted || ''}</td>;
                                                    case 'DaysMining':
                                                        return <td key={col.key} style={style}>{calc?.DaysMining ?? ''}</td>;
                                                    case 'DaysOffline':
                                                        return <td key={col.key} style={style}>{fin?.DaysOffline ?? ''}</td>;
                                                    case 'KasMinedAlready':
                                                        return <td key={col.key} style={style}>{fin?.KasMinedAlready ? fin.KasMinedAlready.toFixed(0) : ''}</td>;
                                                    case 'MinedTotalRecent':
                                                        return <td key={col.key} style={style}>{calc?.MinedTotalRecent ? calc.MinedTotalRecent.toFixed(0) : ''}</td>;
                                                    case 'MinedTotal':
                                                        return <td key={col.key} style={style}>{calc?.MinedTotal ? calc.MinedTotal.toFixed(0) : ''}</td>;
                                                    case 'MinedPerDayRecent':
                                                        return <td key={col.key} style={style}>{calc?.MinedPerDayRecent ? calc.MinedPerDayRecent.toFixed(0) : ''}</td>;
                                                    case 'MinedPerDay':
                                                        return <td key={col.key} style={style}>{calc?.MinedPerDay ? calc.MinedPerDay.toFixed(0) : ''}</td>;
                                                    case 'PurchaseCost':
                                                        return <td key={col.key} style={style}>{fin?.PurchaseCost ? `£${fin.PurchaseCost.toFixed(0)}` : ''}</td>;
                                                    case 'EarnedToDate':
                                                        return <td key={col.key} style={style}>{calc?.EarnedToDate ? `£${calc.EarnedToDate.toFixed(0)}` : ''}</td>;
                                                    case 'StillToEarn':
                                                        return <td key={col.key} style={style}>{calc?.StillToEarn ? `£${calc.StillToEarn.toFixed(0)}` : ''}</td>;
                                                    case 'EarnPerDay':
                                                        return <td key={col.key} style={style}>{calc?.EarnPerDay ? `£${calc.EarnPerDay.toFixed(2)}` : ''}</td>;
                                                    case 'ProfitPerDay':
                                                        return <td key={col.key} style={{ ...style, color: calc?.ProfitPerDay < 0 ? '#ef4444' : undefined }}>{calc?.ProfitPerDay ? `£${calc.ProfitPerDay.toFixed(2)}` : ''}</td>;
                                                    case 'DaysToROI':
                                                        return <td key={col.key} style={style}>{calc?.DaysToROI !== null && calc?.DaysToROI !== undefined ? calc.DaysToROI.toFixed(0) : ''}</td>;
                                                    case 'ROIDate':
                                                        return <td key={col.key} style={style}>{calc?.ROIDate || ''}</td>;
                                                    default:
                                                        return <td key={col.key} style={style}></td>;
                                                }
                                            })}
                                        </tr>
                                    );
                                });
                                // Totals row
                                rows.push(
                                    <tr key="totals" style={{ fontWeight: 'bold', background: '#1e293b', color: '#f59e42' }}>
                                        {selectedColumns.map((col, colIdx) => {
                                            let value = '';
                                            // Right justify all columns after 'Host'
                                            let style = { ...styles.tableCell };
                                            if (colIdx > selectedColumns.findIndex(c => c.key === 'Host')) {
                                                style = { ...styles.tableCell, textAlign: 'right' };
                                            }
                                            if (col.style) {
                                                style = { ...style, ...col.style };
                                            }
                                            switch (col.key) {
                                                case 'Name':
                                                    value = 'Totals';
                                                    style = { ...styles.tableCell };
                                                    break;
                                                case 'ElectricCostPerDay':
                                                    value = `$${formatNumber(totalElectric, 2)}`;
                                                    break;
                                                case 'ElectricCostPerDayGBP':
                                                    value = `£${formatNumber(usdToGbp(totalElectric), 2)}`;
                                                    break;
                                                case 'Hashrate':
                                                    value = `${formatNumber(totalHashrate, 2)} TH/s`;
                                                    break;
                                                case 'HashRatePercent':
                                                    value = '';
                                                    break;
                                                case 'KasMinedAlready':
                                                    value = formatNumber(totalKasMinedOld, 0);
                                                    break;
                                                case 'MinedTotalRecent':
                                                    value = formatNumber(totalMinedRecent, 0);
                                                    break;
                                                case 'MinedTotal':
                                                    value = formatNumber(totalMinedTotal, 0);
                                                    break;
                                                case 'MinedPerDayRecent':
                                                    value = countMinedPerDayRecent > 0 ? formatNumber(totalMinedPerDayRecent, 0) : '';
                                                    break;
                                                case 'BreakEvenKaspa':
                                                    value = (totalBreakEvenDenominator > 0) ? `£${(totalBreakEvenNumerator / totalBreakEvenDenominator).toFixed(2)}` : '';
                                                    break;
                                                case 'MinedPerDay':
                                                    value = countMinedPerDay > 0 ? formatNumber(totalMinedPerDay, 0) : '';
                                                    break;
                                                case 'PurchaseCost':
                                                    value = `$${formatNumber(totalPurchase, 0)}`;
                                                    break;
                                                case 'EarnedToDate':
                                                    value = `£${formatNumber(totalEarnedToDate, 0)}`;
                                                    break;
                                                case 'StillToEarn':
                                                    value = `£${formatNumber(totalStillToEarn, 0)}`;
                                                    break;
                                                case 'EarnPerDay':
                                                    value = `£${formatNumber(totalEarnPerDay, 2)}`;
                                                    break;
                                                case 'ProfitPerDay':
                                                    value = `£${formatNumber(totalProfitPerDay, 2)}`;
                                                    style = { ...style, color: totalProfitPerDay < 0 ? '#ef4444' : undefined };
                                                    break;
                                                case 'DaysToROI':
                                                    value = '';
                                                    break;
                                                case 'ROIDate':
                                                    value = '';
                                                    break;
                                                default:
                                                    value = '';
                                            }
                                            return <td key={col.key} style={style}>{value}</td>;
                                        })}
                                    </tr>
                                );
                                return rows;
                            })()}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );

    function getFinanceForWorker(worker) {
        if (!workerFinance) return null;
        const rawName = worker.name.replace('jjphotos.', '');
        // Map ks0 suffixes to user-friendly names if needed
        let matchName = rawName;
        if (rawName.startsWith('ks0')) {
            const suffix = rawName.slice(3);
            if (suffix === 'Neville') matchName = 'ks02';
            else if (suffix === 'Irwin') matchName = 'ks03';
            else if (suffix === 'Ronaldo') matchName = 'ks07';
        }
        const fin = workerFinance.find(wf => wf.Name === matchName);
        // Add ElectricCostPerDayGBP field if not present
        if (fin && typeof fin.ElectricCostPerDay === 'number') {
            fin.ElectricCostPerDayGBP = usdToGbp(fin.ElectricCostPerDay);
        }
        return fin;
    }
    function parseDateUK(dateStr) {
        // Accepts DD/MM/YYYY or D/M/YYYY
        const [d, m, y] = dateStr.split('/').map(Number);
        return new Date(y, m - 1, d);
    }
};

export default FinanceTable;
